// src/components/NotificationBell.jsx
// Notification bell icon for the Header.
// FIXED: handles both stored notifications (title/body from DB) and
//        derived notifications (text/sub from dynamic derivation).
// FIXED: seen ID uses n.id when available, falls back to n.type + created_at/ts.

import React, { useState, useEffect, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import { apiFetch } from "../utils/api";

// Get a unique, stable ID for a notification (stored or derived)
function getNotifId(n) {
  if (n.id) return String(n.id); // stored notifications have a stable id
  return (n.type || "unknown") + "-" + (n.ts || n.created_at || Math.random());
}

// Get the primary display text from a notification
function getNotifText(n) {
  // Stored DB notifications have title; derived ones have text
  return n.text || n.title || "";
}

// Get the secondary display text from a notification
function getNotifSub(n) {
  // Stored DB notifications have body; derived ones have sub
  return n.sub || n.body || "";
}

// Get an icon for the notification
function getNotifIcon(n) {
  if (n.icon) return n.icon;
  const icons = {
    match_result:    "\uD83C\uDFC6",
    result_submitted: "\uD83D\uDCCA",
    player_joined:   "\u26BD",
    league_join:     "\uD83C\uDFC6",
    game_comment:    "\uD83D\uDCAC",
    announcement_game:   "\uD83D\uDCE2",
    announcement_league: "\uD83D\uDCE2",
    coach_digest:    "\uD83D\uDCCB",
  };
  return icons[n.type] || "\uD83D\uDD14";
}

// Get a link for stored notifications (derived from data field or type)
function getNotifLink(n) {
  if (n.link) return n.link;
  try {
    if (n.data) {
      const d = typeof n.data === "string" ? JSON.parse(n.data) : n.data;
      if (d.league_id) return "/league/" + d.league_id;
      if (d.game_id)   return "/play";
    }
  } catch (e) {}
  if (n.type === "match_result" || n.type === "result_submitted") return "/leagues";
  if (n.type === "coach_digest") return "/coach";
  return "/";
}

export default function NotificationBell({ user }) {
  const [notifications, setNotifications] = useState([]);
  const [unread, setUnread]               = useState(0);
  const [open, setOpen]                   = useState(false);

  const [seenIds, setSeenIds] = useState(function() {
    try {
      const raw = localStorage.getItem("gt_seen_notifs");
      return raw ? JSON.parse(raw) : [];
    } catch (e) { return []; }
  });

  const ref = useRef(null);

  const fetchNotifications = useCallback(async function() {
    if (!user) return;
    // FIXED: read every possible token key. sessionStorage["gt_token"] is
    // cleared when iOS closes the app, so falling back to localStorage
    // ["gt_token"] (set by LoginModal.storeSession) is required for the
    // bell to load anything after the app has been reopened.
    const token = sessionStorage.getItem("gt_token")
                || localStorage.getItem("gt_token")
                || localStorage.getItem("token");
    if (!token) return;
    try {
      const res = await apiFetch("/api/notifications", {
        headers: { "Authorization": "Bearer " + token }
      });
      if (!res.ok) return; // JSON 401 handled but just bail
      const data = await res.json();
      const notes = data.notifications || [];
      setNotifications(notes);
      // Count unseen using stable IDs
      const unseenCount = notes.filter(function(n) {
        return !seenIds.includes(getNotifId(n));
      }).length;
      setUnread(unseenCount);
    } catch (e) {}
  }, [user, seenIds]);

  // Initial fetch + poll every 60s
  useEffect(function() {
    if (!user) { setNotifications([]); setUnread(0); return; }
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 60000);
    return function() { clearInterval(interval); };
  }, [user, fetchNotifications]);

  // Close on outside click
  useEffect(function() {
    if (!open) return;
    function handle(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener("mousedown", handle);
    return function() { document.removeEventListener("mousedown", handle); };
  }, [open]);

  function handleOpen() {
    setOpen(function(v) { return !v; });
    if (!open) {
      // Mark all current as seen using stable IDs
      const ids = notifications.map(getNotifId);
      setSeenIds(function(prev) {
        const merged = Array.from(new Set(prev.concat(ids))).slice(-200);
        try { localStorage.setItem("gt_seen_notifs", JSON.stringify(merged)); } catch (e) {}
        return merged;
      });
      setUnread(0);

      // Also POST to mark as read in DB
      const token = sessionStorage.getItem("gt_token")
                  || localStorage.getItem("gt_token")
                  || localStorage.getItem("token");
      if (token) {
        apiFetch("/api/notifications", {
          method: "POST",
          headers: { "Authorization": "Bearer " + token }
        }).catch(function() {});
      }
    }
  }

  if (!user) return null;

  return (
    <div ref={ref} className="relative">
      <button
        onClick={handleOpen}
        className="relative w-8 h-8 flex items-center justify-center text-zinc-400 hover:text-white transition-colors"
        aria-label="Notifications"
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
          <path d="M13.73 21a2 2 0 0 1-3.46 0" />
        </svg>
        {unread > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 bg-lime-400 text-black text-[10px] font-bold rounded-full flex items-center justify-center px-0.5 leading-none">
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-10 w-80 bg-zinc-900 border border-zinc-700 rounded-xl shadow-2xl z-50 overflow-hidden">
          <div className="px-4 py-3 border-b border-zinc-800 flex items-center justify-between">
            <p className="text-white text-sm font-semibold">Notifications</p>
            <button onClick={function() { setOpen(false); }} className="text-zinc-500 hover:text-white text-lg leading-none">&times;</button>
          </div>

          <div className="max-h-80 overflow-y-auto">
            {notifications.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-zinc-600 text-sm">No notifications yet</p>
                <p className="text-zinc-700 text-xs mt-1">Activity from your games and leagues will appear here</p>
              </div>
            ) : (
              <div className="divide-y divide-zinc-800">
                {notifications.map(function(n, i) {
                  const notifId = getNotifId(n);
                  const isSeen  = seenIds.includes(notifId);
                  const text    = getNotifText(n);
                  const sub     = getNotifSub(n);
                  const icon    = getNotifIcon(n);
                  const link    = getNotifLink(n);

                  if (!text) return null; // skip empty notifications

                  return (
                    <Link
                      key={i}
                      to={link}
                      onClick={function() { setOpen(false); }}
                      className={"flex items-start gap-3 px-4 py-3 hover:bg-zinc-800 transition-colors " + (isSeen ? "opacity-60" : "")}
                    >
                      <span className="text-lg flex-shrink-0 mt-0.5">{icon}</span>
                      <div className="min-w-0 flex-1">
                        <p className={"text-sm leading-snug " + (isSeen ? "text-zinc-400" : "text-white")}>{text}</p>
                        {sub && <p className="text-zinc-500 text-xs mt-0.5 truncate">{sub}</p>}
                      </div>
                      {!isSeen && (
                        <div className="w-2 h-2 rounded-full bg-lime-400 flex-shrink-0 mt-1.5" />
                      )}
                    </Link>
                  );
                })}
              </div>
            )}
          </div>

          <div className="px-4 py-2 border-t border-zinc-800 text-center">
            <button
              onClick={fetchNotifications}
              className="text-zinc-500 hover:text-lime-400 text-xs transition-colors"
            >
              Refresh
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
